var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAlert } from 'components/alert/AlertStack';
import { classNames } from 'libs/react';
import { useQuery, useState } from 'libs/react';
import { useMutation } from '@apollo/client';
import { Button } from 'components/Button';
import { FormAction } from 'components/form/FormAction';
import { FormField } from 'components/form/FormField';
import { FormGroup } from 'components/form/FormGroup';
import { FormInput } from 'components/form/FormInput';
import { FormSection } from 'components/form/FormSection';
import { CollapsibleFormSection } from 'components/form/CollapsibleFormSection';
import { FormSelect } from 'components/form/FormSelect';
import { MutationForm } from 'components/form/graphql/MutationForm';
import { CreateContactMutation } from 'graphql/Contact/mutations/CreateContactMutation';
import { React } from 'libs/react';
import { FormClientSearch } from 'modules/client/forms/FormClientSearch';
import { useAccount } from 'components/account/AccountProvider';
import { useTranslation } from 'components/translation/TranslationProvider';
import { ContactListQuery } from 'graphql/Contact/queries/ContactListQuery';
import { FormGoogleAutocompleteInput } from 'components/form/FormGoogleAutocompleteInput';
import { FormPhone } from 'components/form/FormPhone';
import { CustomFieldForm } from 'components/field/form/CustomFieldForm';
import { FieldGroupsQuery } from 'graphql/FieldGroup/queries/FieldGroupsQuery';
import { UpdateFieldValuesMutation } from 'graphql/FieldValue/mutations/UpdateFieldValuesMutation';
import { formatFieldValueWithTranslation } from 'helpers/Field';
import { getFilledFieldValues } from 'helpers/Field';
import './CreateContactPanel.scss';
/**
 * @component CreateContactPanel
 * @since 1.0.0
 */
export function CreateContactPanel(props) {
    var _a, _b;
    const { style, defaultOrganizationValue, isEndClient } = Object.assign({ style: 'normal' }, props);
    const alert = useAlert();
    const account = useAccount();
    const { customLabels, language } = useTranslation();
    const [search, setSearch] = useState('');
    const [location, setLocation] = useState(null);
    const [fieldGroupsResult] = useQuery(FieldGroupsQuery, {
        variables: {
            input: {
                query: [{ field: 'category', value: 'CONTACT' }]
            }
        }
    });
    const fieldGroups = (_a = fieldGroupsResult === null || fieldGroupsResult === void 0 ? void 0 : fieldGroupsResult.fieldGroups) !== null && _a !== void 0 ? _a : [];
    const [updateFieldValues] = useMutation(UpdateFieldValuesMutation);
    /**
     * @function onClientSearch
     * @since 1.0.0
     * @hidden
     */
    function onClientSearch(search) {
        setSearch(search);
    }
    /**
     * @function onProcess
     * @since 1.0.0
     * @hidden
     */
    function onProcess(values) {
        var _a;
        const { firstname, lastname, contactEmail, client, title, contactLanguage, contactPhone, extension } = values;
        const input = {
            firstname,
            lastname,
            title,
            client,
            primary: true,
            manager: false,
            dynamicSalesFormAssign: false,
            dynamicSalesFormNotify: false,
            language: contactLanguage ||
                ((_a = account.languages.find((language) => language.primary)) === null || _a === void 0 ? void 0 : _a.id),
            locationValues: location
        };
        if (contactEmail) {
            input.emails = [
                {
                    type: 'WORK',
                    email: contactEmail,
                    primary: true
                }
            ];
        }
        if (contactPhone) {
            input.phones = [
                {
                    type: 'WORK',
                    phone: contactPhone,
                    extension: extension,
                    primary: true
                }
            ];
        }
        return {
            variables: {
                input
            },
            refetchQueries: [
                {
                    query: ContactListQuery
                }
            ]
        };
    }
    /**
     * @function onSuccess
     * @since 1.0.0
     * @hidden
     */
    function onSuccess(result, values) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            alert.present({
                title: (React.createElement("p", null,
                    React.createElement("strong", null, (_a = result === null || result === void 0 ? void 0 : result.createContact) === null || _a === void 0 ? void 0 : _a.fullname),
                    ' ',
                    t('has been created.')))
            });
            const filledFields = getFilledFieldValues(values === null || values === void 0 ? void 0 : values.fields);
            if (Object.keys(filledFields).length > 0) {
                try {
                    yield updateFieldValues({
                        variables: {
                            input: {
                                fieldEntity: {
                                    entity: 'CONTACT',
                                    entityId: result.createContact.id
                                },
                                values: formatFieldValueWithTranslation(filledFields, undefined, language, account.languages)
                            }
                        }
                    });
                }
                catch (error) {
                    alert.present({ error: error });
                }
            }
            if (props.onDone) {
                props.onDone(result.createContact);
            }
        });
    }
    /**
     * @function onFailure
     * @since 1.0.0
     * @hidden
     */
    function onFailure(error) {
        alert.present({ error });
    }
    const className = classNames('create-contact-panel', 'create-contact-panel--' + style, props.className);
    return (React.createElement("div", { className: className },
        React.createElement(MutationForm, { name: "contact-create-form", mutation: CreateContactMutation, onProcess: onProcess, onSuccess: onSuccess, onFailure: onFailure, style: style },
            React.createElement(FormSection, { title: `${t(`What's the name of the`)} ${isEndClient
                    ? customLabels['contact'][language]
                    : customLabels['representative'][language]}?`, style: style },
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormInput, { type: "text", name: "firstname", label: t('First Name'), placeholder: t('Enter First Name'), required: true }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormInput, { type: "text", name: "lastname", label: t('Last Name'), placeholder: t('Enter Last Name'), required: true }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormInput, { type: "text", name: "title", label: t('Title'), placeholder: t('Enter Title'), optional: true }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormSelect, { name: "contactLanguage", label: t('Language'), placeholder: t('Select Language'), options: account.languages.map((lang) => ({
                                value: lang.id,
                                label: lang.name
                            })), defaultValue: (_b = account.languages.find((lang) => lang.primary)) === null || _b === void 0 ? void 0 : _b.id, optional: true }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormInput, { type: "email", name: "contactEmail", label: t('Email'), placeholder: t('Enter Email') }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormPhone, { name: "contactPhone", label: t('Phone'), placeholder: t('Enter Phone') }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormInput, { type: "text", name: "extension", label: t('Extension'), placeholder: t('Enter Extension') }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormClientSearch, { label: customLabels['Client'][language], style: style, placeholder: `${t('Search')} ${isEndClient
                                ? customLabels['client'][language]
                                : customLabels['partner'][language]}`, onSearch: onClientSearch, defaultValue: defaultOrganizationValue, isEndClient: isEndClient }))),
                React.createElement(FormGroup, null,
                    React.createElement(FormField, null,
                        React.createElement(FormGoogleAutocompleteInput, { name: "address", label: t('Address'), placeholder: t('Enter Address'), value: location
                                ? (location === null || location === void 0 ? void 0 : location.civicNumber) +
                                    ' ' +
                                    (location === null || location === void 0 ? void 0 : location.road) +
                                    ', ' +
                                    (location === null || location === void 0 ? void 0 : location.city) +
                                    ', ' +
                                    (location === null || location === void 0 ? void 0 : location.province) +
                                    ', ' +
                                    (location === null || location === void 0 ? void 0 : location.country)
                                : '', onLocationSelected: (location) => {
                                setLocation(location);
                            } })))),
            fieldGroups.length > 0 && (React.createElement(CollapsibleFormSection, { title: t('Custom fields') },
                React.createElement(CustomFieldForm, { entity: "CONTACT", fields: fieldGroups, translable: false }))),
            React.createElement(FormAction, { align: "stretch" },
                React.createElement(Button, { style: "primary", label: `${t('Create')} ${isEndClient ? customLabels['contact'][language] : customLabels['representative'][language]}`, badge: "arrow-right", submit: true })))));
}
